import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { FaHeart, FaRegHeart, FaStar, FaMapMarkerAlt, FaSlidersH } from 'react-icons/fa';
import { coachingAPI } from '../services/api';
import { normalizeCoachListItem } from '../utils/coachCrsAdapter';
import { COACH_TIER_BADGE_STYLES } from '../utils/coachUiConstants';
import CoachAvatarImage from '../components/PlayerDashboard/CoachAvatarImage';
import CoachBannerImage from '../components/PlayerDashboard/CoachBannerImage';

const BRAND = { blue: '#043663', red: '#D52B1E' };

const LS_FAV = 'hps_coach_directory_favorites';

const PageWrap = styled.div`
  width: 100%;
  max-width: 100%;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
`;

const Subtitle = styled.p`
  margin: 0 0 1.5rem;
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const CoachSearchRow = styled.div`
  margin: 0 0 1.25rem;
  width: 100%;
  max-width: 36rem;
`;

const CoachSearchInput = styled.input`
  width: 100%;
  padding: 0.65rem 1rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  font-size: 0.95rem;
  color: #0f172a;
  background: #fff;
  &::placeholder {
    color: #94a3b8;
  }
  &:focus {
    outline: none;
    border-color: ${BRAND.blue};
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.12);
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  width: 100%;
`;

const FiltersBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.9rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-weight: 600;
  font-size: 0.875rem;
  color: #334155;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  &:hover {
    border-color: ${BRAND.blue};
    background: #f8fafc;
  }
`;

const Badge = styled.span`
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: ${BRAND.red};
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  font-size: 0.875rem;
  color: #334155;
  background: #fff;
  max-width: 180px;
  &:focus {
    outline: none;
    border-color: ${BRAND.blue};
  }
`;

const SortWrap = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;
`;

const ResultsLine = styled.div`
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 1.25rem;
`;

const Alert = styled.div`
  padding: 1rem 1.25rem;
  border-radius: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
`;

const LoadingState = styled.div`
  padding: 2rem;
  text-align: center;
  color: #64748b;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.15rem;
  width: 100%;
`;

const Card = styled.article`
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(4, 54, 99, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s, transform 0.15s;
  cursor: pointer;
  &:hover {
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.09);
  }
  &:focus-visible {
    outline: 2px solid ${BRAND.blue};
    outline-offset: 2px;
  }
`;

const Banner = styled.div`
  position: relative;
  height: 96px;
  background: linear-gradient(135deg, #0c4a6e 0%, ${BRAND.blue} 55%, #1e3a5f 100%);
  flex-shrink: 0;
  overflow: hidden;
`;

const BannerOverlay = styled.div`
  position: absolute;
  inset: 0;
  padding: 0.55rem 0.75rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  pointer-events: none;
  z-index: 2;
  & > * {
    pointer-events: auto;
  }
`;

const AvailPill = styled.span`
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  background: ${(p) => (p.$available ? '#16a34a' : '#64748b')};
  color: #fff;
`;

const FavBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${BRAND.red};
  flex-shrink: 0;
  &:hover {
    background: #fef2f2;
  }
`;

const CardBody = styled.div`
  padding: 0.65rem 0.95rem 0.9rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const CoachRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #e2e8f0;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NameBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const NameLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
`;

const Name = styled.span`
  font-weight: 700;
  font-size: 0.98rem;
  color: #0f172a;
`;

const TierPill = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.25rem;
  font-size: 0.82rem;
  color: #64748b;
`;

const Tagline = styled.p`
  margin: 0.45rem 0 0.4rem;
  font-size: 0.78rem;
  color: #475569;
  line-height: 1.35;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
`;

const Chip = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
`;

const LocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.82rem;
  color: #64748b;
  margin-top: auto;
  padding-top: 0.5rem;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  margin-top: 0.65rem;
`;

const Price = styled.div`
  font-size: 0.88rem;
  font-weight: 600;
  color: #0f172a;
`;

const Cta = styled.span`
  padding: 0.5rem 0.85rem;
  border-radius: 10px;
  background: ${BRAND.red};
  color: #fff;
  font-weight: 600;
  font-size: 0.8rem;
  white-space: nowrap;
  flex-shrink: 0;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.35rem;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const PageBtn = styled.button`
  min-width: 2.25rem;
  height: 2.25rem;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$active ? BRAND.blue : '#e2e8f0')};
  background: ${(p) => (p.$active ? BRAND.blue : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#334155')};
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  @media (min-width: 640px) {
    align-items: center;
  }
`;

const Drawer = styled.div`
  background: #fff;
  width: 100%;
  max-width: 420px;
  max-height: 85vh;
  overflow-y: auto;
  border-radius: 16px 16px 0 0;
  padding: 1.25rem;
  @media (min-width: 640px) {
    border-radius: 16px;
  }
`;

const DrawerTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: 1.1rem;
`;

const DrawerActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const GhostBtn = styled.button`
  flex: 1;
  padding: 0.65rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-weight: 600;
  cursor: pointer;
`;

const PrimaryDrawerBtn = styled.button`
  flex: 1;
  padding: 0.65rem;
  border-radius: 10px;
  border: none;
  background: ${BRAND.blue};
  color: #fff;
  font-weight: 600;
  cursor: pointer;
`;

const PAGE_SIZE = 6;

function loadFavorites() {
  try {
    const raw = localStorage.getItem(LS_FAV);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveFavorites(set) {
  localStorage.setItem(LS_FAV, JSON.stringify([...set]));
}

function recommendedScore(c) {
  const rating = c.rating_average != null ? Number(c.rating_average) : 0;
  const rc = Number(c.review_count) || 0;
  const clubs = Number(c.affiliated_clubs_count) || 0;
  return rating * Math.log10(10 + rc) + clubs * 0.5;
}

export default function PlayerFindCoachPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qRaw = (searchParams.get('q') || '').trim();
  const qLower = qRaw.toLowerCase();

  const [qDraft, setQDraft] = useState(qRaw);
  const [language, setLanguage] = useState('');
  const [skill, setSkill] = useState('');
  const [sort, setSort] = useState('recommended');
  const [page, setPage] = useState(1);
  const [favorites, setFavorites] = useState(() => loadFavorites());
  const [filtersOpen, setFiltersOpen] = useState(false);

  const skillOptions = useMemo(
    () => [
      { value: '', label: t('playerFindCoachPage.skillAny') },
      { value: 'beginner', label: t('playerFindCoachPage.skillBeginner') },
      { value: 'intermediate', label: t('playerFindCoachPage.skillIntermediate') },
      { value: 'advanced', label: t('playerFindCoachPage.skillAdvanced') },
      { value: 'elite', label: t('playerFindCoachPage.skillElite') },
    ],
    [t]
  );
  const sortOptions = useMemo(
    () => [
      { value: 'recommended', label: t('playerFindCoachPage.sortRecommended') },
      { value: 'name_asc', label: t('playerFindCoachPage.sortNameAsc') },
      { value: 'name_desc', label: t('playerFindCoachPage.sortNameDesc') },
      { value: 'clubs', label: t('playerFindCoachPage.sortClubs') },
    ],
    [t]
  );

  const { data: coaches = [], isLoading, isError, error } = useQuery(
    ['coachingCoaches', qRaw],
    async () => {
      const res = await coachingAPI.listCoaches(qRaw ? { q: qRaw } : {});
      const raw = Array.isArray(res) ? res : [];
      return raw.map(normalizeCoachListItem);
    },
    { staleTime: 60 * 1000, retry: 1 }
  );

  const languageOptions = useMemo(() => {
    const set = new Set();
    coaches.forEach((c) => (c.languages || []).forEach((l) => set.add(l)));
    return ['', ...[...set].sort((a, b) => a.localeCompare(b))];
  }, [coaches]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (language) n++;
    if (skill) n++;
    return n;
  }, [language, skill]);

  const filtered = useMemo(() => {
    let list = [...coaches];

    if (qLower) {
      list = list.filter(
        (c) =>
          c.display_name.toLowerCase().includes(qLower) ||
          (c.tagline && c.tagline.toLowerCase().includes(qLower)) ||
          (c.primary_club_name && c.primary_club_name.toLowerCase().includes(qLower)) ||
          (c.about_me && c.about_me.toLowerCase().includes(qLower))
      );
    }
    if (language) {
      list = list.filter((c) => c.languages.some((l) => l === language));
    }
    if (skill) {
      list = list.filter((c) => c.skill_levels_served.includes(skill));
    }

    if (sort === 'name_asc') {
      list.sort((a, b) => a.display_name.localeCompare(b.display_name));
    } else if (sort === 'name_desc') {
      list.sort((a, b) => b.display_name.localeCompare(a.display_name));
    } else if (sort === 'clubs') {
      list.sort((a, b) => (b.affiliated_clubs_count || 0) - (a.affiliated_clubs_count || 0));
    } else {
      list.sort((a, b) => recommendedScore(b) - recommendedScore(a));
    }

    return list;
  }, [coaches, qLower, language, skill, sort]);

  useEffect(() => {
    setPage(1);
  }, [qLower, language, skill, sort]);

  useEffect(() => {
    setQDraft(qRaw);
  }, [qRaw]);

  const commitCoachSearch = useCallback(() => {
    const v = qDraft.trim();
    setSearchParams(v ? { q: v } : {}, { replace: true });
  }, [qDraft, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const slice = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const toggleFav = useCallback((id, e) => {
    e.stopPropagation();
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveFavorites(next);
      return next;
    });
  }, []);

  const clearFilters = () => {
    setLanguage('');
    setSkill('');
  };

  const errMsg =
    isError && error?.response?.data?.error
      ? error.response.data.error
      : isError
        ? t('playerFindCoachPage.loadErrorFallback')
        : null;

  return (
    <PageWrap>
      <Title>{t('playerFindCoachPage.title')}</Title>
      <Subtitle>
        {t('playerFindCoachPage.subtitle')}
      </Subtitle>

      <CoachSearchRow>
        <CoachSearchInput
          type="search"
          placeholder={t('playerFindCoachPage.placeholderSearch')}
          value={qDraft}
          onChange={(e) => setQDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitCoachSearch();
            }
          }}
          aria-label={t('playerFindCoachPage.ariaSearch')}
        />
      </CoachSearchRow>

      {errMsg && <Alert role="alert">{errMsg}</Alert>}

      <Toolbar>
        <FiltersBtn type="button" onClick={() => setFiltersOpen(true)}>
          <FaSlidersH />
          {t('playerFindCoachPage.filters')}
          {activeFilterCount > 0 && <Badge>{activeFilterCount}</Badge>}
        </FiltersBtn>

        <Select value={language} onChange={(e) => setLanguage(e.target.value)} aria-label={t('playerFindCoachPage.languageAll')}>
          {languageOptions.map((lang) => (
            <option key={lang || 'all'} value={lang}>
              {lang ? lang : t('playerFindCoachPage.languageAll')}
            </option>
          ))}
        </Select>
        <Select value={skill} onChange={(e) => setSkill(e.target.value)} aria-label={t('playerFindCoachPage.skillAny')}>
          {skillOptions.map((o) => (
            <option key={o.value || 'any'} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>

        <SortWrap>
          <span>{t('playerFindCoachPage.sortBy')}</span>
          <Select value={sort} onChange={(e) => setSort(e.target.value)} aria-label={t('playerFindCoachPage.sortRecommended')}>
            {sortOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </SortWrap>
      </Toolbar>

      <ResultsLine>
        {isLoading ? t('playerFindCoachPage.loading') : t('playerFindCoachPage.resultsFound', { count: filtered.length })}
      </ResultsLine>

      {isLoading ? (
        <LoadingState>{t('playerFindCoachPage.loadingCoaches')}</LoadingState>
      ) : (
        <Grid>
          {slice.map((c) => {
            const tier = COACH_TIER_BADGE_STYLES[c.coach_tier] || COACH_TIER_BADGE_STYLES.coach;
            const isFav = favorites.has(c.id);
            const isAvail = c.is_available_booking === true;
            const loc =
              c.primary_club_name && c.city
                ? `${c.primary_club_name}, ${c.city}`
                : c.primary_club_name ||
                  (c.affiliated_clubs_count
                    ? t('playerFindCoachPage.partnerClubs', { count: c.affiliated_clubs_count })
                    : t('playerFindCoachPage.partnerClubsFallback'));
            const goBook = () => navigate(`/player/coaches/${c.id}/book`);
            return (
              <Card
                key={c.id}
                tabIndex={0}
                role="link"
                aria-label={t('playerFindCoachPage.ariaViewSessions', { name: c.display_name })}
                onClick={(e) => {
                  if (e.target.closest('[data-coach-directory-fav]')) return;
                  goBook();
                }}
                onKeyDown={(e) => {
                  if (e.target.closest('[data-coach-directory-fav]')) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goBook();
                  }
                }}
              >
                <Banner>
                  <CoachBannerImage coachId={c.id} hasBannerImage={c.has_banner_image} />
                  <BannerOverlay>
                    <AvailPill $available={isAvail}>{c.availability_label}</AvailPill>
                    <FavBtn
                      type="button"
                      data-coach-directory-fav
                      onClick={(e) => toggleFav(c.id, e)}
                      aria-label={isFav ? t('playerFindCoachPage.ariaRemoveFav') : t('playerFindCoachPage.ariaAddFav')}
                    >
                      {isFav ? <FaHeart /> : <FaRegHeart />}
                    </FavBtn>
                  </BannerOverlay>
                </Banner>
                <CardBody>
                  <CoachRow>
                    <Avatar>
                      <CoachAvatarImage
                        coachId={c.id}
                        photoUrl={c.profile_photo_url}
                        hasPhotoImage={c.has_photo_image}
                        alt=""
                      />
                    </Avatar>
                    <NameBlock>
                      <NameLine>
                        <Name>{c.display_name}</Name>
                        <TierPill $bg={tier.bg} $color={tier.color}>
                          {c.coach_tier_label}
                        </TierPill>
                      </NameLine>
                      {c.rating_average != null ? (
                        <RatingRow>
                          <FaStar color="#f59e0b" />
                          <strong>{Number(c.rating_average).toFixed(1)}</strong>
                          <span>({c.review_count})</span>
                        </RatingRow>
                      ) : (
                        <RatingRow style={{ color: '#94a3b8' }}>{t('playerFindCoachPage.ratingWhenAvailable')}</RatingRow>
                      )}
                    </NameBlock>
                  </CoachRow>
                  <Tagline>{c.tagline}</Tagline>
                  <Chips>
                    {[...c.languages.slice(0, 2), ...c.specialties.slice(0, 2)].map((t) => (
                      <Chip key={t}>{t}</Chip>
                    ))}
                  </Chips>
                  <LocRow>
                    <FaMapMarkerAlt />
                    <span>{loc}</span>
                  </LocRow>
                  <FooterRow>
                    <Price>
                      {c.price_from_amount != null
                        ? t('playerFindCoachPage.fromPrice', {
                            currency: c.currency,
                            amount: Number.isInteger(c.price_from_amount)
                              ? c.price_from_amount
                              : Number(c.price_from_amount).toFixed(2),
                          })
                        : t('playerFindCoachPage.pricingOnBooking')}
                    </Price>
                    <Cta aria-hidden>{t('playerFindCoachPage.viewSessions')}</Cta>
                  </FooterRow>
                </CardBody>
              </Card>
            );
          })}
        </Grid>
      )}

      {!isLoading && !errMsg && filtered.length === 0 && (
        <LoadingState>{t('playerFindCoachPage.emptyFilters')}</LoadingState>
      )}

      {totalPages > 1 && (
        <Pagination>
          <PageBtn
            type="button"
            disabled={pageSafe <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </PageBtn>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
            <PageBtn
              key={num}
              type="button"
              $active={num === pageSafe}
              onClick={() => setPage(num)}
            >
              {num}
            </PageBtn>
          ))}
          <PageBtn
            type="button"
            disabled={pageSafe >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            ›
          </PageBtn>
        </Pagination>
      )}

      {filtersOpen && (
        <Overlay role="presentation" onClick={() => setFiltersOpen(false)}>
          <Drawer onClick={(e) => e.stopPropagation()}>
            <DrawerTitle>{t('playerFindCoachPage.drawerTitle')}</DrawerTitle>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <Select value={language} onChange={(e) => setLanguage(e.target.value)}>
                {languageOptions.map((lang) => (
                  <option key={lang || 'all'} value={lang}>
                    {lang ? lang : t('playerFindCoachPage.languageAll')}
                  </option>
                ))}
              </Select>
              <Select value={skill} onChange={(e) => setSkill(e.target.value)}>
                {skillOptions.map((o) => (
                  <option key={o.value || 'any'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
            <DrawerActions>
              <GhostBtn type="button" onClick={clearFilters}>
                {t('playerFindCoachPage.clear')}
              </GhostBtn>
              <PrimaryDrawerBtn type="button" onClick={() => setFiltersOpen(false)}>
                {t('playerFindCoachPage.apply')}
              </PrimaryDrawerBtn>
            </DrawerActions>
          </Drawer>
        </Overlay>
      )}
    </PageWrap>
  );
}
